import React, { ComponentType } from 'react';
import ReactDOM from 'react-dom';
import { importRemote } from 'module-federation-import-remote';
import { BridgeComponent, createRemoteComponent } from 'mfe-react-bridge';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import federatedModules from '../../federatedModules';
import ErrorPage from './ErrorPage';

const queryClient = new QueryClient();

const renderErrorPage = (error: Error, attempt = 0) => {
  const container = document.getElementById('error-container');
  if (!container && attempt < 3) {
    setTimeout(() => renderErrorPage(error, attempt + 1), 500);
    return;
  }

  if (container) {
    ReactDOM.render(
      <QueryClientProvider client={queryClient}>
        <ErrorPage errorCode={(error as CustomHttpError).statusCode} />,
      </QueryClientProvider>,
      container,
    );
  }
};

type PageProps = {
  Component: React.ComponentType<ModuleComponentProps>;
  path: string;
  hideIntercom?: boolean;
  customAuth?: boolean;
  sentryDsn?: string;
  loadBeforePortal?: boolean;
  configcatProjectKey?: string;
};

interface RemoteComponent {
  default: ComponentType<ModuleComponentProps>;
  BRIDGE: undefined;
}

interface RemoteBridgeComponent {
  default: () => BridgeComponent<ModuleComponentProps>;
  BRIDGE: true;
}

type Remote = RemoteComponent | RemoteBridgeComponent;

const pages: PageProps[] = federatedModules.map(
  ({ packageName, name, component, ...props }) => ({
    Component: React.lazy(async () => {
      const remote = await importRemote<Remote>({
        url: `/bundles/${name}`,
        scope: packageName,
        module: component,
      });
      if (remote.BRIDGE) {
        return {
          default: createRemoteComponent({
            remote,
            fallback: (info: { error: Error }) => {
              renderErrorPage(info.error);
              return <div id="error-container" />;
            },
          }),
        };
      }
      return remote;
    }),
    ...props,
  }),
);

export default pages;
